import type { ReactNode } from 'react'
import { ArrowUpTrayIcon } from '@heroicons/react/16/solid'
import { useDispatch } from '../../../Flux/index.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { DROPZONE_ACCEPT } from '../../../Services/OrderWizard/wizardFiles.ts'
import { dropFiles } from '../../../Services/OrderWizard/dropFiles.ts'
import { dropDisabled, resolveDropPool } from '../../../Services/OrderWizard/dropTargets.ts'
import { useFilePicker } from './useFilePicker.tsx'

/**
 * Click-to-browse for files: the whole area opens the file picker, its button does from the keyboard.
 * Dragged files go to the page overlay, which covers this area while a drag is on.
 */
export const DropArea = ({
  onFiles,
  disabled,
  slim,
  testId,
  children
}: {
  onFiles: (files: File[]) => void
  disabled?: boolean
  slim?: boolean
  testId: string
  /** The prompt; `browse` is the button that opens the picker. */
  children: (browse: ReactNode) => ReactNode
}) => {
  const picker = useFilePicker({ accept: DROPZONE_ACCEPT, disabled, onFiles, testId: `${testId}-input` })
  const browse = (
    <button
      type="button"
      className="link link-hover font-medium text-slate-800 disabled:cursor-not-allowed disabled:text-slate-400"
      disabled={disabled}
      data-testid={`${testId}-browse`}
      onClick={(event) => {
        event.stopPropagation()
        picker.open()
      }}
    >
      {slim ? 'click to add' : 'browse'}
    </button>
  )
  return (
    <div
      data-testid={testId}
      className={`flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-600 transition-colors hover:border-slate-400 ${
        slim ? 'px-3 py-2' : 'flex-col justify-center px-4 py-6 text-center'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
      onClick={picker.open}
    >
      <ArrowUpTrayIcon className={`shrink-0 text-slate-400 ${slim ? 'h-4 w-4' : 'h-6 w-6'}`} aria-hidden />
      <span>{children(browse)}</span>
      {picker.input}
    </div>
  )
}

/** The start screen's way to add files; every file goes to the order as a whole. */
export const WizardDropzone = ({ draft, label }: { draft: DraftState; label: string }) => {
  const dispatch = useDispatch()
  return (
    <DropArea testId="wizard-dropzone" disabled={dropDisabled(draft)} onFiles={(files) => dropFiles(draft.id, files, dispatch)}>
      {(browse) => (
        <>
          <span className="block font-medium text-slate-800">{label}</span>
          <span className="block">Anywhere on the page, or {browse}. FASTQ files (.fastq.gz); a sample sheet as CSV, TSV or XLSX.</span>
        </>
      )}
    </DropArea>
  )
}

/** The Files step's way to add more: a slim bar, adding to the first pool like the overlay's default. */
export const SlimDropBar = ({ draft, prompt }: { draft: DraftState; prompt?: string }) => {
  const dispatch = useDispatch()
  return (
    <DropArea
      slim
      testId="wizard-drop-bar"
      disabled={dropDisabled(draft)}
      onFiles={(files) => dropFiles(draft.id, files, dispatch, resolveDropPool(draft, undefined))}
    >
      {(browse) => (
        <>
          {prompt ?? 'Drop more files here'} or {browse}
        </>
      )}
    </DropArea>
  )
}
